import { logError } from '@ptek/client-logger';
import { useCallback } from 'react';
import {
  DIALOG_TYPES,
  SESSION_DISCARD_ACTIONS,
  SessionDiscardActionType,
  useDialogStore,
} from '../../../stores/dialogStore';

interface UseSessionDiscardFlowProps {
  onDocumentCleared?: () => void | Promise<void>;
}

export type ResumeOrDiscardResult = 'continue' | 'discard' | 'cancel';

export const useSessionDiscardFlow = (
  props: UseSessionDiscardFlowProps = {}
) => {
  const { onDocumentCleared } = props;
  const { openDialog } = useDialogStore();

  const checkSessionDiscard = useCallback(
    async (hasTranscript: boolean, hasDocument: boolean): Promise<boolean> => {
      if (!hasTranscript && !hasDocument) {
        return true;
      }

      try {
        const result = await openDialog<{
          action: SessionDiscardActionType;
        }>(DIALOG_TYPES.SESSION_DISCARD, {
          hasTranscript,
          hasDocument,
        });

        const shouldDiscard = result.action === SESSION_DISCARD_ACTIONS.DISCARD;

        if (shouldDiscard && onDocumentCleared) {
          await onDocumentCleared();
        }

        return shouldDiscard;
      } catch (error) {
        logError(
          'useSessionDiscardFlow',
          'Error showing session discard dialog:',
          error
        );
        return false;
      }
    },
    [openDialog, onDocumentCleared]
  );

  const checkResumeOrDiscard =
    useCallback(async (): Promise<ResumeOrDiscardResult> => {
      try {
        const result = await openDialog<{
          action: SessionDiscardActionType;
        }>(DIALOG_TYPES.SESSION_DISCARD, {
          hasTranscript: false,
          hasDocument: false,
          canContinue: true,
        });

        if (result.action === SESSION_DISCARD_ACTIONS.CONTINUE) {
          return 'continue';
        }
        if (result.action === SESSION_DISCARD_ACTIONS.DISCARD) {
          return 'discard';
        }
        return 'cancel';
      } catch (error) {
        logError(
          'useSessionDiscardFlow',
          'Error showing resume or discard dialog:',
          error
        );
        return 'cancel';
      }
    }, [openDialog]);

  return { checkSessionDiscard, checkResumeOrDiscard };
};
